import React, { useState, useRef, useEffect } from 'react'
import AppDropdown from '../../../components/AppDropdown'
import { createEmailTemplate, updateEmailTemplate } from '../api/emailSettingsApi'

const AVAILABLE_TAGS = [
  { tag: '#EmployeeName#', desc: 'Name of the submitting employee' },
  { tag: '#RequestID#', desc: 'Unique request / approval ID' },
  { tag: '#ApproverName#', desc: 'Current assigned reviewer or approver' },
  { tag: '#OriginalApproverName#', desc: 'Initial primary approver before escalation' },
  { tag: '#PendingHours#', desc: 'Hours elapsed since submission' },
  { tag: '#EntryDate#', desc: 'Timesheet entry or event date' },
  { tag: '#LeaveDate#', desc: 'Leave application date' },
  { tag: '#LeaveType#', desc: 'Full Day / Half Day / Casual' },
  { tag: '#HoursLogged#', desc: 'Total hours logged in timesheet' },
  { tag: '#Details#', desc: 'Task description, reason, or remarks' },
  { tag: '#ApprovalLink#', desc: 'Direct URL to review & decide the request' },
  { tag: '#CompanyName#', desc: 'Tenant company organization name' },
  { tag: '#Person#', desc: 'Greeting recipient name' },
  { tag: '#Date#', desc: 'Current system date' },
]

const CATEGORY_OPTIONS = [
  { value: 'APPROVAL', label: 'APPROVAL (Approvals & Escalations)' },
  { value: 'HR', label: 'HR (Leaves, Onboarding, Payroll)' },
  { value: 'PROJECT', label: 'PROJECT (Tasks, Milestones)' },
  { value: 'GENERAL', label: 'GENERAL (System, Alerts)' },
]

const SAMPLE_CONTEXT = {
  EmployeeName: 'Alexander Hayes',
  RequestID: '10482',
  ApproverName: 'Jane Smith',
  OriginalApproverName: 'Robert Vance',
  PendingHours: '28.5',
  EntryDate: '2026-10-20',
  LeaveDate: '2026-10-24',
  LeaveType: 'FULL_DAY',
  HoursLogged: '8.5',
  Details: 'Client Milestone 3 Deliverable Review & Code Deployment',
  ApprovalLink: 'https://bosglobalerp.com/timesheets/10482',
  CompanyName: 'Boss Global ERP',
  Person: 'Alexander',
  Date: '2026-10-20',
}

export default function EmailTemplateEditorModal({ template, isOpen, onClose, onSaved }) {
  const isEditing = Boolean(template?.id)
  const isSystem = Boolean(template?.isSystemTemplate ?? template?.is_system_template)

  const [form, setForm] = useState({
    templateCode: '',
    templateName: '',
    category: 'APPROVAL',
    subject: '',
    htmlContent: '',
    textContent: '',
  })
  const [viewMode, setViewMode] = useState('VISUAL')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const editorRef = useRef(null)

  useEffect(() => {
    if (template) {
      setForm({
        templateCode: template.templateCode || template.template_code || '',
        templateName: template.templateName || template.template_name || '',
        category: template.category || 'APPROVAL',
        subject: template.subject || '',
        htmlContent: template.htmlContent || template.html_content || '',
        textContent: template.textContent || template.text_content || '',
      })
    } else {
      setForm({
        templateCode: '',
        templateName: '',
        category: 'APPROVAL',
        subject: '',
        htmlContent: '<p>Hello <strong>#ApproverName#</strong>,</p><p>Please review the pending request <strong>##RequestID#</strong> for <strong>#EmployeeName#</strong>.</p>',
        textContent: '',
      })
    }
    setViewMode('VISUAL')
    setError(null)
  }, [template, isOpen])

  useEffect(() => {
    if (viewMode === 'VISUAL' && editorRef.current) {
      if (editorRef.current.innerHTML !== form.htmlContent) {
        editorRef.current.innerHTML = form.htmlContent || ''
      }
    }
  }, [viewMode, isOpen])

  if (!isOpen) return null

  function handleEditorInput() {
    if (editorRef.current) {
      setForm((prev) => ({ ...prev, htmlContent: editorRef.current.innerHTML }))
    }
  }

  function executeCommand(command, value = null) {
    if (viewMode !== 'VISUAL') return
    editorRef.current?.focus()
    document.execCommand(command, false, value)
    handleEditorInput()
  }

  function insertTag(tagString) {
    if (viewMode === 'VISUAL') {
      executeCommand('insertText', tagString)
    } else if (viewMode === 'HTML') {
      setForm((prev) => ({ ...prev, htmlContent: (prev.htmlContent || '') + tagString }))
    }
  }

  function insertTagIntoSubject(tagString) {
    setForm((prev) => ({ ...prev, subject: (prev.subject || '') + ' ' + tagString }))
  }

  function renderPreviewHtml(html) {
    let out = html || ''
    Object.entries(SAMPLE_CONTEXT).forEach(([k, v]) => {
      const hashRegex = new RegExp(`#${k}#`, 'gi')
      const braceRegex = new RegExp(`{${k}}`, 'gi')
      out = out.replace(hashRegex, v).replace(braceRegex, v)
    })
    return out
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError(null)

    const code = form.templateCode.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_')
    if (!code) {
      setError('Template Code is required.')
      setSaving(false)
      return
    }
    if (!form.templateName.trim()) {
      setError('Template Name is required.')
      setSaving(false)
      return
    }
    if (!form.subject.trim()) {
      setError('Subject is required.')
      setSaving(false)
      return
    }

    const payload = {
      templateCode: code,
      templateName: form.templateName.trim(),
      category: form.category,
      subject: form.subject.trim(),
      htmlContent: form.htmlContent || '',
      textContent: form.textContent || null,
    }

    try {
      if (isEditing) {
        await updateEmailTemplate(template.id, payload)
      } else {
        await createEmailTemplate(payload)
      }
      onSaved?.()
      onClose?.()
    } catch (err) {
      setError('Error saving template: ' + (err?.response?.data?.detail || err.message))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="es-modal-backdrop" onClick={onClose}>
      <div className="es-modal-content rounded-3xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="es-modal-header border-b border-outline-variant/15 p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-lg">
                {isEditing ? 'edit_note' : 'add_circle'}
              </span>
            </span>
            <h3 className="font-display text-sm font-extrabold text-on-surface">
              {isEditing ? `Edit Template: ${form.templateName}` : 'Create Custom Email Template'}
            </h3>
            {isSystem && (
              <span className="rounded-xl bg-primary/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-primary">
                System Template
              </span>
            )}
          </div>
          <button
            type="button"
            className="text-on-surface-variant hover:text-on-surface p-1"
            onClick={onClose}
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="es-modal-body p-6">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-xl text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-200">
              <span className="material-symbols-outlined text-base">error</span>
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Area (8 cols): Form & Editor */}
            <div className="lg:col-span-8 flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                    Template Code (Unique Key) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary disabled:opacity-60"
                    placeholder="e.g. TIMESHEET_ESCALATION_L1"
                    value={form.templateCode}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        templateCode: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
                      }))
                    }
                    disabled={isSystem}
                    required
                  />
                  {isSystem && (
                    <span className="text-[10px] text-on-surface-variant">System template codes are locked for platform integrity.</span>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                    Category <span className="text-rose-500">*</span>
                  </label>
                  <AppDropdown
                    value={form.category}
                    onChange={(val) => setForm((prev) => ({ ...prev, category: String(val) }))}
                    options={CATEGORY_OPTIONS}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  Template Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary"
                  placeholder="e.g. Timesheet Approval - L1 Escalation"
                  value={form.templateName}
                  onChange={(e) => setForm((prev) => ({ ...prev, templateName: e.target.value }))}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                    Email Subject Line <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[10px] text-on-surface-variant">
                    Tip: Right-click tag on right to append
                  </span>
                </div>
                <input
                  type="text"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary"
                  placeholder="e.g. [URGENT] Approval Required: #{RequestID} for {EmployeeName}"
                  value={form.subject}
                  onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                  required
                />
              </div>

              {/* Editor Mode Tabs & Toolbar */}
              <div className="mt-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                    Email Body Content <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-lg text-[11px] font-bold transition ${
                        viewMode === 'VISUAL'
                          ? 'bg-primary text-on-primary shadow-xs'
                          : 'bg-surface-container-low text-on-surface hover:bg-surface-container-high'
                      }`}
                      onClick={() => setViewMode('VISUAL')}
                    >
                      Visual Editor
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-lg text-[11px] font-bold transition ${
                        viewMode === 'HTML'
                          ? 'bg-primary text-on-primary shadow-xs'
                          : 'bg-surface-container-low text-on-surface hover:bg-surface-container-high'
                      }`}
                      onClick={() => setViewMode('HTML')}
                    >
                      HTML Code
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-lg text-[11px] font-bold transition ${
                        viewMode === 'PREVIEW'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-surface-container-low text-on-surface hover:bg-surface-container-high'
                      }`}
                      onClick={() => setViewMode('PREVIEW')}
                    >
                      Live Preview
                    </button>
                  </div>
                </div>

                {viewMode === 'VISUAL' && (
                  <div className="border border-outline-variant/30 rounded-2xl overflow-hidden">
                    <div className="flex flex-wrap items-center gap-1 bg-surface-container-low p-2 border-b border-outline-variant/20">
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded font-bold text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('bold')}
                        title="Bold"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded italic text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('italic')}
                        title="Italic"
                      >
                        I
                      </button>
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded underline text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('underline')}
                        title="Underline"
                      >
                        U
                      </button>
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded line-through text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('strikeThrough')}
                        title="Strikethrough"
                      >
                        S
                      </button>
                      <div className="h-4 w-px bg-outline-variant/30 mx-1" />
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded font-bold text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('formatBlock', '<h2>')}
                        title="Heading 2"
                      >
                        H2
                      </button>
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded font-bold text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('formatBlock', '<h3>')}
                        title="Heading 3"
                      >
                        H3
                      </button>
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('formatBlock', '<p>')}
                        title="Paragraph"
                      >
                        ¶
                      </button>
                      <div className="h-4 w-px bg-outline-variant/30 mx-1" />
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('insertUnorderedList')}
                        title="Bullet List"
                      >
                        • List
                      </button>
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded text-xs hover:bg-surface-container-high text-on-surface"
                        onClick={() => executeCommand('insertOrderedList')}
                        title="Numbered List"
                      >
                        1. List
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 rounded hover:bg-surface-container-high text-on-surface flex items-center"
                        onClick={() => {
                          const url = prompt('Enter link URL:')
                          if (url) executeCommand('createLink', url)
                        }}
                        title="Insert Link"
                      >
                        <span className="material-symbols-outlined text-sm">link</span>
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 rounded hover:bg-surface-container-high text-on-surface flex items-center"
                        onClick={() => executeCommand('removeFormat')}
                        title="Clear Formatting"
                      >
                        <span className="material-symbols-outlined text-sm">format_clear</span>
                      </button>
                    </div>
                    <div
                      ref={editorRef}
                      className="min-h-[260px] max-h-[380px] overflow-y-auto p-4 text-xs leading-relaxed text-on-surface focus:outline-none bg-surface-container-lowest"
                      contentEditable
                      onInput={handleEditorInput}
                      onBlur={handleEditorInput}
                    />
                  </div>
                )}

                {viewMode === 'HTML' && (
                  <textarea
                    className="w-full rounded-2xl border border-outline-variant/30 bg-surface-container-low p-3.5 font-mono text-xs leading-relaxed text-on-surface focus:outline-none focus:border-primary"
                    rows="12"
                    value={form.htmlContent}
                    onChange={(e) => setForm((prev) => ({ ...prev, htmlContent: e.target.value }))}
                  />
                )}

                {viewMode === 'PREVIEW' && (
                  <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low p-5 max-h-[380px] overflow-y-auto">
                    <div className="bg-surface-container-lowest border border-outline-variant/20 rounded-xl p-4 shadow-xs">
                      <div className="border-b border-outline-variant/15 pb-2.5 mb-3">
                        <span className="text-[10px] uppercase font-bold text-on-surface-variant">SUBJECT:</span>
                        <p className="font-bold text-xs text-on-surface mt-0.5">
                          {renderPreviewHtml(form.subject)}
                        </p>
                      </div>
                      <div
                        className="text-xs leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: renderPreviewHtml(form.htmlContent) }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Area (4 cols): TIPS Panel matching TEZ ERP */}
            <div className="lg:col-span-4 flex flex-col gap-4">
              <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-4">
                <div className="flex items-center gap-2 text-primary font-bold text-xs border-b border-outline-variant/15 pb-2.5 mb-3">
                  <span className="material-symbols-outlined text-sm">tag</span>
                  <span>TIPS: DYNAMIC VARIABLES</span>
                </div>
                <p className="text-[11px] text-on-surface-variant mb-3 leading-relaxed">
                  Click any tag below to insert into body, or right-click to append to subject:
                </p>

                <div className="flex flex-wrap gap-1.5">
                  {AVAILABLE_TAGS.map((t) => (
                    <button
                      key={t.tag}
                      type="button"
                      className="px-2 py-1 rounded-lg border border-outline-variant/30 bg-surface-container-lowest text-[10px] font-mono font-bold text-primary hover:bg-primary/10 transition select-none"
                      onClick={() => insertTag(t.tag)}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        insertTagIntoSubject(t.tag)
                      }}
                      title={`${t.desc} (Click: insert in body, Right-click: append to subject)`}
                    >
                      {t.tag}
                    </button>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-outline-variant/15 text-[10px] text-on-surface-variant leading-relaxed">
                  <strong>Interpolation:</strong> Both <code>#Tag#</code> and <code>{'{Tag}'}</code> syntax are automatically filled with live request data during dispatch.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="es-modal-footer border-t border-outline-variant/15 p-4 flex justify-end gap-3 bg-surface-container-low/30">
          <button
            type="button"
            className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition disabled:opacity-50"
            onClick={handleSubmit}
            disabled={saving}
          >
            <span className={`material-symbols-outlined text-base ${saving ? 'animate-spin' : ''}`}>
              {saving ? 'sync' : 'save'}
            </span>
            <span>{isEditing ? 'Save Template Changes' : 'Create Template'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
